/**
 * QuestProgressTracker
 * 
 * Compact progress indicator for active quests
 * Shows campaign progress, quest state (items, allies, knowledge)
 */

import React, { useState } from 'react';
import { QuestPlaythrough } from '../utils/questManager';
import './QuestProgressTracker.css';

export interface QuestProgressTrackerProps {
  questPlaythrough: QuestPlaythrough;
}

export const QuestProgressTracker: React.FC<QuestProgressTrackerProps> = ({
  questPlaythrough,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const currentSection = questPlaythrough.sections?.[questPlaythrough.sections.length - 1];
  
  // Quest progress (estimate 12 sections total)
  const estimatedSections = 12;
  const completedSections = questPlaythrough.sections?.length || 0;
  const questProgress = (completedSections / estimatedSections) * 100;
  
  // Extract quest state items
  const questState = questPlaythrough.questState || {};
  const stateEntries = Object.entries(questState).filter(([_, value]) => value !== false);
  
  // Categorize state
  const items = stateEntries.filter(([key]) => key.toLowerCase().includes('has') || key.toLowerCase().includes('item'));
  const allies = stateEntries.filter(([key]) => key.toLowerCase().includes('met') || key.toLowerCase().includes('allied') || key.toLowerCase().includes('saved'));
  const knowledge = stateEntries.filter(([key]) => key.toLowerCase().includes('knows') || key.toLowerCase().includes('learned') || key.toLowerCase().includes('discovered'));
  const other = stateEntries.filter(([key]) => 
    !items.some(([k]) => k === key) &&
    !allies.some(([k]) => k === key) &&
    !knowledge.some(([k]) => k === key)
  );
  
  return (
    <div className={`quest-progress-tracker ${isExpanded ? 'expanded' : 'collapsed'}`}>
      {/* Header - Always visible */}
      <div className="quest-progress-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="quest-progress-title">
          <span className="quest-progress-icon">📜</span>
          <span className="quest-progress-name">Quest</span>
        </div>
        <div className="quest-progress-summary">
          Section {currentSection?.sectionNumber || 0}/{estimatedSections}
        </div>
        <button className="quest-progress-toggle" aria-label={isExpanded ? "Collapse" : "Expand"}>
          {isExpanded ? '▼' : '▶'}
        </button>
      </div>
      
      {/* Progress Bar */}
      <div className="quest-progress-bar-container">
        <div className="quest-progress-bar">
          <div 
            className="quest-progress-bar-fill"
            style={{ width: `${questProgress}%` }}
          />
        </div>
        <div className="quest-progress-percentage">
          {Math.round(questProgress)}%
        </div>
      </div>
      
      {/* Expanded State Display */}
      {isExpanded && stateEntries.length > 0 && (
        <div className="quest-progress-state">
          {items.length > 0 && (
            <div className="quest-state-category">
              <h4>🎒 Items</h4>
              <ul>
                {items.map(([key, value]) => (
                  <li key={key}>{formatStateName(key)}: {String(value)}</li>
                ))}
              </ul>
            </div>
          )}
          
          {allies.length > 0 && (
            <div className="quest-state-category">
              <h4>🤝 Allies</h4>
              <ul>
                {allies.map(([key, value]) => (
                  <li key={key}>{formatStateName(key)}: {String(value)}</li>
                ))}
              </ul>
            </div>
          )}
          
          {knowledge.length > 0 && (
            <div className="quest-state-category">
              <h4>📚 Knowledge</h4>
              <ul>
                {knowledge.map(([key, value]) => (
                  <li key={key}>{formatStateName(key)}: {String(value)}</li>
                ))}
              </ul>
            </div>
          )}
          
          {other.length > 0 && (
            <div className="quest-state-category">
              <h4>✨ Other</h4>
              <ul>
                {other.map(([key, value]) => (
                  <li key={key}>{formatStateName(key)}: {String(value)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      
      {isExpanded && stateEntries.length === 0 && (
        <div className="quest-progress-state-empty">
          <p>No quest state tracked yet.</p>
        </div>
      )}
    </div>
  );
};

// Helper to format camelCase state names
function formatStateName(name: string): string {
  return name
    .replace(/([A-Z])/g, ' $1') // Add space before capitals
    .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
    .trim();
}

